/**
 * Connection detail page - view config, mapping, sync history
 */

import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Link2,
  Play,
  TestTube,
  Trash2,
} from 'lucide-react'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useIdPConnection,
  useDeleteIdPConnection,
  useTestIdPConnection,
  useTriggerSync,
  useIdPSyncHistory,
} from '@/hooks/use-idp-connections'
import {
  ConnectionStatusBadge,
  SyncStatusBadge,
} from '@/components/identity/sync-status-badge'

export function ConnectionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: connection, isLoading } = useIdPConnection(id)
  const { data: syncHistoryData, isLoading: syncHistoryLoading } = useIdPSyncHistory(id)
  const deleteConnection = useDeleteIdPConnection()
  const testConnection = useTestIdPConnection()
  const triggerSync = useTriggerSync()

  const syncHistory = syncHistoryData?.data ?? []

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!connection) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <p className="text-muted-foreground">Connection not found.</p>
      </div>
    )
  }

  const mapping = connection.attributeMapping ?? {}

  return (
    <>
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Button variant="ghost" size="sm" onClick={() => navigate('/identity/connections')}>
            <ArrowLeft className="mr-1 h-3.5 w-3.5" />
            Connections
          </Button>
          <Separator orientation="vertical" className="h-4" />
          <Link2 className="h-4 w-4" />
          <h1 className="text-base font-medium">{connection.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => testConnection.mutate(connection.id)}
            disabled={testConnection.isPending}
          >
            <TestTube className="mr-2 h-3.5 w-3.5" />
            Test
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => triggerSync.mutate(connection.id)}
            disabled={triggerSync.isPending || connection.status === 'SYNCING'}
          >
            <Play className="mr-2 h-3.5 w-3.5" />
            Sync Now
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => deleteConnection.mutate(connection.id)}
          >
            <Trash2 className="mr-2 h-3.5 w-3.5" />
            Delete
          </Button>
        </div>
      </header>

      <div className="flex flex-1 flex-col gap-4 p-4 md:p-6">
        {/* Connection Info */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Connection Details</CardTitle>
              <ConnectionStatusBadge status={connection.status} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <Label className="text-xs text-muted-foreground">Provider Type</Label>
                <p className="text-sm">{connection.providerType}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Description</Label>
                <p className="text-sm">{connection.description ?? '-'}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Sync Enabled</Label>
                <p className="text-sm">{connection.syncEnabled ? 'Yes' : 'No'}</p>
              </div>
              {connection.syncSchedule && (
                <div>
                  <Label className="text-xs text-muted-foreground">Schedule</Label>
                  <p className="text-sm font-mono">{connection.syncSchedule}</p>
                </div>
              )}
              {connection.syncFilter && (
                <div>
                  <Label className="text-xs text-muted-foreground">Filter</Label>
                  <p className="text-sm font-mono">{connection.syncFilter}</p>
                </div>
              )}
              <div>
                <Label className="text-xs text-muted-foreground">Created</Label>
                <p className="text-sm">{new Date(connection.createdAt).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Attribute Mapping */}
        <Card>
          <CardHeader>
            <CardTitle>Attribute Mapping</CardTitle>
            <CardDescription>How IdP fields map to poli-x attributes</CardDescription>
          </CardHeader>
          <CardContent>
            {Object.keys(mapping).length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                No attribute mappings configured.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source Field (IdP)</TableHead>
                    <TableHead>Target Attribute (poli-x)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(mapping).map(([source, target]) => (
                    <TableRow key={source}>
                      <TableCell className="font-mono text-xs">{source}</TableCell>
                      <TableCell className="font-mono text-xs">{target}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Sync History */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Sync History</CardTitle>
                <CardDescription>Recent synchronization runs</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {syncHistoryLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : syncHistory.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                No sync runs yet.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead>Errors</TableHead>
                    <TableHead>Started</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {syncHistory.map((run) => (
                    <TableRow key={run.id}>
                      <TableCell>
                        <SyncStatusBadge status={run.status} />
                      </TableCell>
                      <TableCell className="text-xs">{run.trigger}</TableCell>
                      <TableCell className="text-xs">{run.totalSubjects}</TableCell>
                      <TableCell className="text-xs">{run.subjectsCreated}</TableCell>
                      <TableCell className="text-xs">{run.subjectsUpdated}</TableCell>
                      <TableCell className="text-xs">
                        {run.errorCount > 0 ? (
                          <span className="text-destructive">{run.errorCount}</span>
                        ) : (
                          '0'
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {run.startedAt
                          ? new Date(run.startedAt).toLocaleString()
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
